'use client';

import Image from 'next/image';
import { useEffect, useRef, useState, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const projects = [
  {
    id: 1,
    image: '/images/salon-showcase-1.webp',
    alt: 'Modern salon tasarımı örneği',
    title: '',
    subtitle: '',
  },
  {
    id: 2,
    image: '/images/slider4.webp',
    alt: 'Ferah kuaför salonu görseli',
    title: '',
    subtitle: '',
  },
  {
    id: 3,
    image: '/images/slider2.webp',
    alt: 'Berber salonu referansı',
    title: '',
    subtitle: '',
  },
  {
    id: 4,
    image: '/images/slider3.webp',
    alt: 'Berber salonu referansı 2',
    title: '',
    subtitle: '',
  },
];

const SHOWCASE_IMAGE_QUALITY = 85;

interface ProjectShowcaseProps {
  className?: string;
  id?: string;
}

const ProjectShowcase = ({ className, id }: ProjectShowcaseProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const autoplayRef = useRef<NodeJS.Timeout | null>(null);

  const nextSlide = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % projects.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + projects.length) % projects.length);
  }, []);

  useEffect(() => {
    // Üzerine gelindiğinde otomatik kaymayı durdur
    if (isHovered) return;

    autoplayRef.current = setInterval(() => {
      nextSlide();
    }, 5000);

    return () => {
      if (autoplayRef.current) clearInterval(autoplayRef.current);
    };
  }, [isHovered, nextSlide]);

  return (
    <section
      id={id}
      className={cn('relative w-full overflow-hidden group bg-black', className)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Slider Track */}
      <div
        className="flex w-full transition-transform duration-700 ease-in-out"
        style={{ transform: `translate3d(-${currentIndex * 100}%, 0, 0)` }}
      >
        {projects.map((project, index) => (
          <div
            key={project.id}
            className="relative w-full flex-shrink-0 aspect-[1140/380] bg-black"
          >
            <Image
              src={project.image}
              alt={project.alt}
              fill
              sizes="100vw"
              className="object-cover"
              quality={SHOWCASE_IMAGE_QUALITY}
              priority={index === 0}
              loading={index === 0 ? 'eager' : 'lazy'}
            />
            {/* Metin İçeriği - Sağ Alt Köşede, İnce ve Küçük */}
            <div className="absolute bottom-6 right-6 sm:bottom-10 sm:right-10 flex flex-col items-end text-right px-4 md:px-0 max-w-xs sm:max-w-md lg:max-w-lg">
              <h2 className="text-white text-lg sm:text-2xl md:text-3xl font-light tracking-wide drop-shadow-md mb-1 sm:mb-2">
                {project.title}
              </h2>
              <p className="text-white/90 text-xs sm:text-sm md:text-base font-normal drop-shadow-sm">
                {project.subtitle}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Oklar (Hover durumunda görünür) */}
      <button
        onClick={prevSlide}
        className="absolute left-4 sm:left-8 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-14 sm:h-14 lg:w-16 lg:h-16 flex items-center justify-center rounded-full border-2 border-white/50 text-white/70 opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-black/30 hover:text-white hover:border-white hover:scale-110 z-10"
        aria-label="Önceki Görsel"
      >
        <ChevronLeft className="w-6 h-6 sm:w-8 sm:h-8 lg:w-10 lg:h-10" />
      </button>

      <button
        onClick={nextSlide}
        className="absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-14 sm:h-14 lg:w-16 lg:h-16 flex items-center justify-center rounded-full border-2 border-white/50 text-white/70 opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-black/30 hover:text-white hover:border-white hover:scale-110 z-10"
        aria-label="Sonraki Görsel"
      >
        <ChevronRight className="w-6 h-6 sm:w-8 sm:h-8 lg:w-10 lg:h-10" />
      </button>

      {/* Sayfalama Noktaları (Sol Alt) */}
      <div className="absolute bottom-6 left-6 sm:bottom-10 sm:left-10 flex items-center gap-2 sm:gap-3 z-10">
        {projects.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className="group/dot p-2"
            aria-label={`Slayt ${index + 1}`}
          >
            <span
              className={cn(
                'block rounded-full transition-all duration-300 shadow-sm',
                currentIndex === index
                  ? 'bg-white w-2.5 h-2.5 sm:w-3 sm:h-3 lg:w-4 lg:h-4'
                  : 'bg-white/50 w-2 h-2 sm:w-2.5 sm:h-2.5 lg:w-3 lg:h-3 group-hover/dot:bg-white/80 group-hover/dot:scale-110'
              )}
            />
          </button>
        ))}
      </div>
    </section>
  );
};

export default ProjectShowcase; 